import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { BehaviorSubject, from, lastValueFrom, Observable, TimeoutError } from 'rxjs';
import { switchMap, tap, timeout, catchError } from 'rxjs/operators';
import { ContactService } from './contact.service';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { Preferences } from '@capacitor/preferences';
import jwt_decode from 'jwt-decode';
import { DeviceService } from './device.service';
import { Platform } from '@ionic/angular/standalone';
import { DBService } from './DBService.service';
import { ErrorHandlerUtil } from 'src/app/utils/error-handler.util';
import { CacheService } from 'src/app/shared/services/cache.service';
import { BiometricService } from './biometric.service';

const TOKEN_KEY = 'access-token';
const REFRESH_TOKEN_KEY = 'refresh-token';
const AUTH_TIMEOUT_MS = 30000; // 30 seconds timeout for auth requests

@Injectable({ providedIn: 'root' })
export class AuthService {
  public isAuthenticated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  public locationCount: BehaviorSubject<number> = new BehaviorSubject<number>(
    1
  );
  currentAccessToken = null;
  currentLocationId = 0;

  constructor(
    private http: HttpClient,
    public contactService: ContactService,
    private router: Router,
    private deviceService: DeviceService,
    private platform: Platform,
    private dbService: DBService,
    private cacheService: CacheService,
    private biometricService: BiometricService
  ) {
    // loadToken() is now called via APP_INITIALIZER in main.ts
    // This ensures auth state is ready before the app bootstraps
  }

  // Load accessToken on startup
  async loadToken() {
    const token = await Preferences.get({ key: TOKEN_KEY });
    if (token && token.value) {
      this.currentAccessToken = token.value;
      this.contactService.fetchCurrentContact();
      this.isAuthenticated.next(true);
    } else {
      this.isAuthenticated.next(false);
    }
  }

  async getToken(): Promise<string> {
    const token = await Preferences.get({ key: TOKEN_KEY });
    return token?.value || null;
  }

  // G1 — SMS-first activation: validates texted OTP code and returns a JWT.
  // Backend: POST /api/auth/mobile/activate (mirrors mobile/resetPassword pattern).
  // Endpoint does not exist yet; the page shows an error until it is deployed.
  activateMobile(email: string, code: string): Promise<any> {
    // Contract: POST /api/auth/mobile/activate expects { email, activationCode } (AB#30018 G1)
    return lastValueFrom(
      this.http.post<any>(`${environment.authUrl}/api/auth/mobile/activate`, { email, activationCode: Number(code) }).pipe(
        timeout(AUTH_TIMEOUT_MS),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            throw new Error('Connection timed out. Please check your internet connection and try again.');
          }
          throw error;
        }),
        switchMap((data: any) => {
          this.currentAccessToken = data.accessToken;
          const decoded: any = jwt_decode(this.currentAccessToken);
          this.locationCount.next(decoded.Locations * 1);
          this.currentLocationId = decoded.LocationId;
          this.contactService.fetchCurrentContact();
          const storeAccess = Preferences.set({ key: TOKEN_KEY, value: data.accessToken });
          const storeRefresh = Preferences.set({ key: REFRESH_TOKEN_KEY, value: data.refreshToken });
          return from(Promise.all([storeAccess, storeRefresh]));
        }),
        tap(() => this.isAuthenticated.next(true)),
      )
    );
  }

  doResetPassword(value) {
    return lastValueFrom(
      this.http
        .post<any>(`${environment.authUrl}/api/auth/resetPassword`, value)
        .pipe(
          timeout(AUTH_TIMEOUT_MS),
          catchError((error) => {
            if (error instanceof TimeoutError) {
              throw new Error('Connection timed out. Please check your internet connection and try again.');
            }
            throw error;
          })
        )
    );
  }

  doRegister(value) {
    return new Promise<any>((resolve, reject) => {
      this.http
        .post<any>(`${environment.authUrl}/api/auth/confirmUserEmail`, value)
        .toPromise()
        .then(
          (res: any) => {
            // Success
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  // ???
doForgot(value) {
  return lastValueFrom(
    this.http.post<any>(`${environment.authUrl}/api/auth/mobile/forgotPassword`, value).pipe(
      timeout(AUTH_TIMEOUT_MS),
      catchError((error) => {
        if (error instanceof TimeoutError) {
          throw new Error('Connection timed out. Please check your internet connection and try again.');
        }
        throw error;
      })
    )
  ).catch(err => Promise.reject(ErrorHandlerUtil.extractError(err)));
}

  login(username, password) {
    return this.http
      .post(`${environment.authUrl}/api/auth/authenticate`, {
        username,
        password,
      })
      .pipe(
        timeout(AUTH_TIMEOUT_MS),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            throw new Error('Connection timed out. Please check your internet connection and try again.');
          }
          throw error;
        }),
        switchMap((data: any) => {
          this.currentAccessToken = data.accessToken;
          const decoded: any = jwt_decode(this.currentAccessToken);
          this.locationCount.next(decoded.Locations * 1);
          this.currentLocationId = decoded.LocationId;
          this.contactService.fetchCurrentContact();
          const storeAccess = Preferences.set({
            key: TOKEN_KEY,
            value: data.accessToken,
          });
          const storeRefresh = Preferences.set({
            key: REFRESH_TOKEN_KEY,
            value: data.refreshToken,
          });
          return from(Promise.all([storeAccess, storeRefresh]));
        }),
        tap((_) => {
          this.isAuthenticated.next(true);
        })
      );
  }

  getContactRegisterUrl(code: number) {
    return new Promise<any>((resolve, reject) => {
      this.http
        .get<any>(
          `${environment.resourceUrl}/api/auth/contactRegisterUrl/` + code
        )
        .toPromise()
        .then(
          (res: any) => {
            resolve(res);
          },
          (err) => {
            reject(err);
          }
        );
    });
  }

  async doLogout() {
    this.isAuthenticated.next(false);
    this.currentAccessToken = null;
    this.currentLocationId = 0;
    this.locationCount.next(0);

    await Preferences.remove({ key: TOKEN_KEY });
    await Preferences.remove({ key: REFRESH_TOKEN_KEY });

    // Clear all cached data to ensure fresh state on next login
    this.cacheService.clearAll();

    // Reset contact data
    this.contactService.clearCurrentContact();

    // NOTE: We do NOT clear biometric credentials on logout
    // Credentials remain encrypted on device for quick biometric login
    // They are only cleared when password changes (in reset-password flow)
    // This matches behavior of banking apps and password managers
    console.log('[AuthService] Logout complete (biometric credentials preserved)');

    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  async changeLocation(locationId: number) {
    console.log('🔄 Switching to location:', locationId);

    // Clear ALL cache when switching locations to prevent showing stale data
    console.log('🗑️ Clearing all cached data before location switch...');
    this.cacheService.clearAll();

    let body = {};
    await this.fetchTokens(locationId).then((result: any) => {
      body = result;
    });

    return this.http
      .post(`${environment.authUrl}/api/auth/location/change`, body, {
        withCredentials: true,
      })
      .pipe(
        switchMap(async (data: any) => {
          this.currentAccessToken = data.accessToken;
          this.contactService.fetchCurrentContact();

          // IMPORTANT: Store tokens FIRST
          await Promise.all([
            Preferences.set({ key: TOKEN_KEY, value: data.accessToken }),
            Preferences.set({ key: REFRESH_TOKEN_KEY, value: data.refreshToken }),
          ]);

          // THEN update locationId in cache service (reads from stored token)
          await this.cacheService.updateLocationId();
          console.log('✅ Cache service updated with new locationId:', this.cacheService.getLocationId());

          return data;
        }),
        switchMap((result) => from([result]))
      );
  }

  getNewAccessToken() {
    // console.log("get new access token");

    const storedTokens = Promise.all([
      Preferences.get({ key: TOKEN_KEY }),
      Preferences.get({ key: REFRESH_TOKEN_KEY }),
    ]);
    // console.log(storedTokens);
    return from(storedTokens).pipe(
      switchMap((tokens) => {
        if (tokens) {
          let body = {
            AccessToken: tokens[0].value,
            RefreshToken: tokens[1].value,
          };
          return this.http.post(
            `${environment.authUrl}/api/auth/refresh`,
            body,
            { withCredentials: true }
          );
        } else {
          of(null);
        }
      })
    );
  }

  // Store a new access token
  storeAccessToken(data) {
    console.log('store access token');
    this.currentAccessToken = data.accessToken;
    const storeAccess = Preferences.set({
      key: TOKEN_KEY,
      value: data.accessToken,
    });
    const storeRefresh = Preferences.set({
      key: REFRESH_TOKEN_KEY,
      value: data.refreshToken,
    });

  this.platform.ready().then(async () => {
    try {
      const storedInfo = await this.dbService.getDeviceInfo();
      const storedFcmToken = storedInfo?.fcmToken || '';
      await this.deviceService.publishDeviceInfo(storedFcmToken);
    } catch (e) {
      console.warn('Could not publish device info during token storage:', e);
    }
  });

    return from(Promise.all([storeAccess, storeRefresh]));
  }

  async fetchTokens(locationId?: number) {
    const token = await Preferences.get({ key: TOKEN_KEY });
    const refreshToken = await Preferences.get({ key: REFRESH_TOKEN_KEY });
    return {
      AccessToken: token.value,
      RefreshToken: refreshToken.value,
      LocationId: locationId,
    };
  }

  doReset(value) {
    return lastValueFrom(
      this.http
        .post<any>(`${environment.authUrl}/api/auth/mobile/resetPassword`, value)
        .pipe(
          timeout(AUTH_TIMEOUT_MS),
          catchError((error) => {
            if (error instanceof TimeoutError) {
              throw new Error('Connection timed out. Please check your internet connection and try again.');
            }
            throw error;
          })
        )
    );
  }

  private async getStoredFcmToken(): Promise<string | null> {
  try {
    const info = await this.dbService.getDeviceInfo();
    return info?.fcmToken || null;
  } catch (e) {
    console.error('Error reading stored FCM token', e);
    return null;
  }
}

}
